import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const validatePassword = (pw) => ({
    minLength: pw.length >= 8,
    hasUpper: /[A-Z]/.test(pw),
    hasLower: /[a-z]/.test(pw),
    hasSymbol: /[^A-Za-z0-9]/.test(pw),
});

export default function CustomerRegister() {
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', passwordConfirm: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register, googleLogin } = useCustomerAuth();
    const navigate = useNavigate();

    const pwRules = validatePassword(form.password);
    const allPwValid = pwRules.minLength && pwRules.hasUpper && pwRules.hasLower && pwRules.hasSymbol;

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!allPwValid) {
            return setError('Şifre en az 8 karakter, büyük harf, küçük harf ve sembol içermelidir');
        }
        if (form.password !== form.passwordConfirm) {
            return setError('Şifreler eşleşmiyor');
        }

        setLoading(true);
        try {
            const result = await register({
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone,
                password: form.password
            });
            if (result.requiresVerification) {
                navigate('/eposta-dogrula');
            } else {
                navigate('/');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Kayıt sırasında bir hata oluştu');
        }
        setLoading(false);
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-card-left">
                    <img src="/logo_noora.png" alt="Noora Privé" className="auth-brand-logo" />
                    <p className="auth-brand-tagline">Zarafeti keşfedin</p>
                </div>
                <div className="auth-card-right">
                    <div className="auth-header">
                        <h1>Üye Ol</h1>
                        <p>Hemen ücretsiz hesap oluşturun</p>
                    </div>

                    {error && <div className="auth-error">{error}</div>}

                    <div className="auth-google-wrap">
                        <GoogleLogin
                            onSuccess={async (credentialResponse) => {
                                setError('');
                                setLoading(true);
                                try {
                                    await googleLogin(credentialResponse.credential);
                                    navigate('/');
                                } catch (err) {
                                    setError(err.response?.data?.error || 'Google ile kayıt başarısız oldu');
                                }
                                setLoading(false);
                            }}
                            onError={() => setError('Google ile kayıt başarısız oldu')}
                            text="signup_with"
                            shape="rectangular"
                            size="large"
                            width="100%"
                            locale="tr"
                        />
                    </div>

                    <div className="auth-divider">
                        <span>veya</span>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="auth-row">
                            <div className="auth-field">
                                <label>Ad</label>
                                <input name="firstName" placeholder="Adınız" value={form.firstName} onChange={handleChange} required />
                            </div>
                            <div className="auth-field">
                                <label>Soyad</label>
                                <input name="lastName" placeholder="Soyadınız" value={form.lastName} onChange={handleChange} required />
                            </div>
                        </div>
                        <div className="auth-field">
                            <label>E-posta</label>
                            <input name="email" type="email" placeholder="ornek@email.com" value={form.email} onChange={handleChange} required />
                        </div>
                        <div className="auth-field">
                            <label>Telefon <span className="auth-optional">(opsiyonel)</span></label>
                            <input name="phone" type="tel" placeholder="05XX XXX XX XX" value={form.phone} onChange={handleChange} />
                        </div>
                        <div className="auth-field">
                            <label>Şifre</label>
                            <div className="auth-input-wrap">
                                <input
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="En az 8 karakter"
                                    value={form.password}
                                    onChange={handleChange}
                                    required
                                />
                                <button type="button" className="auth-toggle-pw" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        {form.password && (
                            <div className="pw-rules">
                                <div className={`pw-rule ${pwRules.minLength ? 'valid' : ''}`}>{pwRules.minLength ? <Check size={11}/> : <X size={11}/>} 8+ karakter</div>
                                <div className={`pw-rule ${pwRules.hasUpper ? 'valid' : ''}`}>{pwRules.hasUpper ? <Check size={11}/> : <X size={11}/>} Büyük harf</div>
                                <div className={`pw-rule ${pwRules.hasLower ? 'valid' : ''}`}>{pwRules.hasLower ? <Check size={11}/> : <X size={11}/>} Küçük harf</div>
                                <div className={`pw-rule ${pwRules.hasSymbol ? 'valid' : ''}`}>{pwRules.hasSymbol ? <Check size={11}/> : <X size={11}/>} Sembol</div>
                            </div>
                        )}
                        <div className="auth-field">
                            <label>Şifre Tekrar</label>
                            <div className="auth-input-wrap">
                                <input
                                    name="passwordConfirm"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Şifrenizi tekrar girin"
                                    value={form.passwordConfirm}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <button type="submit" className="auth-submit" disabled={loading}>
                            {loading ? 'Kayıt yapılıyor...' : 'Üye Ol'}
                        </button>
                    </form>

                    <div className="auth-footer">
                        Zaten hesabınız var mı? <Link to="/giris">Giriş Yap</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
